import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

export const useProjects = () => {
  const { user } = useAuth()
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (user) {
      loadProjects()
    } else {
      setProjects([])
      setLoading(false)
    }
  }, [user])

  const loadProjects = async () => {
    try {
      setLoading(true)
      setError(null)

      // Query projects with explicit filtering for dashboard isolation
      // This ensures users only see their own projects in the dashboard
      // while preserving the ability for public projects to be viewed on profile pages
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('owner_id', user.id) // Explicit filter for own projects only
        .order('updated_at', { ascending: false })

      if (error) throw error

      // For now, just use the thumbnail_url directly to avoid complex queries
      const enhancedProjects = (data || []).map(project => ({
        ...project,
        computedThumbnail: project.thumbnail_url || null
      }))

      setProjects(enhancedProjects)
    } catch (err) {
      console.error('Error loading projects:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const createProject = async (projectData) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .insert([{
          ...projectData,
          owner_id: user.id
        }])
        .select()
        .single()

      if (error) throw error

      // Create initial version
      await supabase.rpc('create_project_version', {
        p_project_id: data.id,
        p_description: 'Initial version',
        p_changes: []
      })

      await loadProjects()
      return { data, error: null }
    } catch (err) {
      console.error('Error creating project:', err)
      return { data: null, error: err }
    }
  }

  const updateProject = async (projectId, updates) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .update(updates)
        .eq('id', projectId)
        .select()
        .single()

      if (error) throw error

      await loadProjects()
      return { data, error: null }
    } catch (err) {
      console.error('Error updating project:', err)
      return { data: null, error: err }
    }
  }

  const deleteProject = async (projectId) => {
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId)

      if (error) throw error

      await loadProjects()
      return { error: null }
    } catch (err) {
      console.error('Error deleting project:', err)
      return { error: err }
    }
  }

  const duplicateProject = async (projectId) => {
    try {
      // Get original project with slides and elements
      const { data: originalProject, error: projectError } = await supabase
        .from('projects')
        .select(`
          *,
          slides(
            *,
            elements(*)
          )
        `)
        .eq('id', projectId)
        .single()

      if (projectError) throw projectError

      // Create new project with all metadata
      const { data: newProject, error: newProjectError } = await supabase
        .from('projects')
        .insert([{
          title: `${originalProject.title} (Copy)`,
          description: originalProject.description,
          thumbnail_url: originalProject.thumbnail_url, // Copy thumbnail
          tags: originalProject.tags, // Copy tags
          status: 'draft',
          owner_id: user.id,
          metadata: originalProject.metadata // Copy any additional metadata
        }])
        .select()
        .single()

      if (newProjectError) throw newProjectError

      // Duplicate slides and elements
      for (const slide of originalProject.slides) {
        const { data: newSlide, error: slideError } = await supabase
          .from('slides')
          .insert([{
            project_id: newProject.id,
            slide_order: slide.slide_order,
            slide_type: slide.slide_type,
            background: slide.background,
            metadata: slide.metadata
          }])
          .select()
          .single()

        if (slideError) throw slideError

        // Duplicate elements
        if (slide.elements?.length > 0) {
          const elementsToInsert = slide.elements.map(element => ({
            slide_id: newSlide.id,
            element_type: element.element_type,
            content: element.content,
            style: element.style,
            position: element.position,
            z_index: element.z_index
          }))

          const { error: elementsError } = await supabase
            .from('elements')
            .insert(elementsToInsert)

          if (elementsError) throw elementsError
        }
      }

      await loadProjects()
      return { data: newProject, error: null }
    } catch (err) {
      console.error('Error duplicating project:', err)
      return { data: null, error: err }
    }
  }

  const uploadThumbnail = async (projectId, file) => {
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}/${projectId}/thumbnail.${fileExt}`
      
      // Upload to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('project-thumbnails')
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type
        })

      if (uploadError) throw uploadError

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('project-thumbnails')
        .getPublicUrl(fileName)

      // Update project with thumbnail URL
      const { error: updateError } = await supabase
        .from('projects')
        .update({ thumbnail_url: publicUrl })
        .eq('id', projectId)

      if (updateError) throw updateError

      return { data: publicUrl, error: null }
    } catch (err) {
      console.error('Error uploading thumbnail:', err)
      return { data: null, error: err }
    }
  }

  const inviteCollaborator = async (projectId, email, role = 'viewer') => {
    try {
      const { data, error } = await supabase.rpc('invite_collaborator', {
        p_project_id: projectId,
        p_email: email,
        p_role: role
      })

      if (error) throw error

      await loadProjects()
      return { data, error: null }
    } catch (err) {
      console.error('Error inviting collaborator:', err)
      return { data: null, error: err }
    }
  }

  return {
    projects,
    loading,
    error,
    loadProjects,
    createProject,
    updateProject,
    deleteProject,
    duplicateProject,
    uploadThumbnail,
    inviteCollaborator
  }
}